import { EmailHistory } from '../../email/EmailHistory';
import { EmailSend } from '../../email/EmailSend';
import AssignmentReturnRounded from '@mui/icons-material/AssignmentReturnRounded';
import PrintRounded from '@mui/icons-material/PrintRounded';
import UndoRounded from '@mui/icons-material/UndoRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  Link,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router';
import { api, unwrap } from '../../../api/client';
import { useAuth } from '../../../auth/AuthProvider';
import { Money, Qty } from '../../../components/Money';
import { businessDate, dateTime, money } from '../../../lib/format';
import { useNotify } from '../../../lib/notify';
import { useShop } from '../../../shop/ShopProvider';
import {
  DetailHeader,
  DetailSkeleton,
  ErrorState,
  InfoRow,
  ReasonDialog,
  SummaryCard,
  SummaryGrid,
} from '../components/DocParts';
import { afterSaleKeys, idem, salesKeys } from '../lib/api';
import { deliveryDoc, shopHeader } from '../print/builders';
import { A4Document } from '../print/documents';
import { usePrint } from '../print/usePrint';
import { WhatsappHistory, WhatsappSend } from '../../whatsapp';
import { AttachmentsPanel } from '../../attachments';
import { useShopLogo } from '../../../lib/images';
import { MoreMenu } from '../../../components/MoreMenu';
import { useLinked, useLinkedOne } from '../../../components/useLinked';

export function DeliveryDetailPage() {
  const { id = '' } = useParams();
  const { can } = useAuth();
  const navigate = useNavigate();
  const { settings, format } = useShop();
  const logo = useShopLogo();
  const notify = useNotify();
  const qc = useQueryClient();
  const { print, element } = usePrint();
  const [reverseOpen, setReverseOpen] = useState(false);
  const key = [...salesKeys.deliveries, id];

  const query = useQuery({
    queryKey: key,
    queryFn: () => unwrap(api.GET('/deliveries/{id}', { params: { path: { id } } })),
  });
  const order = useQuery({
    queryKey: [...salesKeys.orders, query.data?.salesOrderId ?? ''],
    queryFn: () =>
      unwrap(
        api.GET('/sales-orders/{id}', { params: { path: { id: query.data?.salesOrderId ?? '' } } }),
      ),
    enabled: !!query.data?.salesOrderId,
  });
  // Goods come back against an invoice: the posted invoices billing this delivery.
  const billed = useQuery({
    queryKey: [...salesKeys.invoices, 'of-delivery', id],
    queryFn: () =>
      unwrap(
        api.GET('/sales-invoices', {
          params: { query: { deliveryId: id, status: 'POSTED', pageSize: 100 } },
        }),
      ),
    enabled: can('sales.return'),
  });
  const [returnMenu, setReturnMenu] = useState<HTMLElement | null>(null);
  const linkedOrder = useLinkedOne('order', query.data?.salesOrderId);
  const invoices = useLinked('invoices', { deliveryId: id });
  const returns = useLinked('salesReturns', { deliveryId: id });

  const reverse = useMutation({
    mutationFn: (reason: string) =>
      unwrap(
        api.POST('/deliveries/{id}/reverse', {
          params: { path: { id }, header: idem() },
          body: { version: query.data?.version ?? 1, reason },
        }),
      ),
    onSuccess: async (d) => {
      qc.setQueryData(key, d);
      notify.success(`Delivery ${d.number} reversed — stock is back`);
      await Promise.all(afterSaleKeys.map((k) => qc.invalidateQueries({ queryKey: [...k] })));
    },
  });

  if (query.isLoading) return <DetailSkeleton />;
  if (query.isError || !query.data)
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;

  const d = query.data;
  const reversed = d.status === 'REVERSED';
  const showCost = d.totalCost !== null;

  return (
    <Box>
      <DetailHeader
        back={{ to: '/sales/deliveries', label: 'Deliveries' }}
        title={d.number}
        status={{ status: d.status }}
        subtitle={`${d.contact.name} · ${businessDate(d.docDate, format)}`}
        actions={
          <>
            <Button
              variant="contained"
              startIcon={<PrintRounded />}
              onClick={() =>
                print(
                  'a4',
                  <A4Document
                    shop={shopHeader(settings, undefined, logo)}
                    doc={deliveryDoc(d, format, order.data?.number)}
                  />,
                )
              }
            >
              Delivery note
            </Button>
            <WhatsappSend
              target={{
                documentType: 'delivery',
                documentId: d.id,
                documentRef: d.number,
                contact: d.contact,
                vars: {
                  number: d.number,
                  name: d.contact.name,
                  date: businessDate(d.docDate, format),
                },
                pdf: {
                  fileName: `${d.number}.pdf`,
                  paper: 'a4',
                  render: () => (
                    <A4Document
                      shop={shopHeader(settings, undefined, logo)}
                      doc={deliveryDoc(d, format, order.data?.number)}
                    />
                  ),
                },
              }}
            />
            <EmailSend
              target={{
                documentType: 'delivery',
                documentId: d.id,
                documentRef: d.number,
                contact: d.contact,
                vars: {
                  number: d.number,
                  name: d.contact.name,
                  date: businessDate(d.docDate, format),
                },
                pdf: {
                  fileName: `${d.number}.pdf`,
                  paper: 'a4',
                  render: () => (
                    <A4Document
                      shop={shopHeader(settings, undefined, logo)}
                      doc={deliveryDoc(d, format, order.data?.number)}
                    />
                  ),
                },
              }}
            />
            {!reversed && can('sales.return') && (
              <Tooltip
                title={
                  billed.data?.data.length === 0
                    ? 'Invoice it first — goods are returned against the invoice. Or reverse the whole delivery.'
                    : ''
                }
              >
                <span>
                  <Button
                    variant="outlined"
                    startIcon={<AssignmentReturnRounded />}
                    disabled={!billed.data?.data.length}
                    onClick={(e) => {
                      const invoices = billed.data?.data ?? [];
                      if (invoices.length === 1)
                        void navigate(`/returns/sales/new?invoiceId=${invoices[0]?.id ?? ''}`);
                      else setReturnMenu(e.currentTarget);
                    }}
                  >
                    Return
                  </Button>
                </span>
              </Tooltip>
            )}
            <Menu anchorEl={returnMenu} open={!!returnMenu} onClose={() => setReturnMenu(null)}>
              <ListSubheader sx={{ lineHeight: '32px' }}>
                Return against which invoice?
              </ListSubheader>
              {(billed.data?.data ?? []).map((inv) => (
                <MenuItem
                  key={inv.id}
                  onClick={() => void navigate(`/returns/sales/new?invoiceId=${inv.id}`)}
                >
                  <ListItemText
                    primary={inv.number}
                    secondary={`${businessDate(inv.docDate, format)} · ${money(inv.total, format)}`}
                  />
                </MenuItem>
              ))}
            </Menu>
            {!reversed && can('sales.cancel') && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<UndoRounded />}
                onClick={() => setReverseOpen(true)}
              >
                Reverse
              </Button>
            )}
            <MoreMenu linked={[linkedOrder, invoices, returns]} />
          </>
        }
      />

      {reversed && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Reversed {d.reversedAt ? dateTime(d.reversedAt, format) : ''}
          {d.reverseReason ? ` — “${d.reverseReason}”` : ''}. The stock went back at the cost it
          left with.
        </Alert>
      )}

      <SummaryGrid>
        <SummaryCard label="Lines" value={String(d.lines.length)} />
        <SummaryCard label="From" value={d.location.name} />
        <SummaryCard
          label="Source"
          value={d.salesOrderId ? (order.data?.number ?? 'Sales order') : 'Counter sale'}
          hint={
            d.salesOrderId ? (
              <Link component={RouterLink} to={`/sales/orders/${d.salesOrderId}`}>
                Open order
              </Link>
            ) : undefined
          }
        />
        <SummaryCard
          label="Cost of goods"
          value={showCost ? <Money value={d.totalCost} /> : '—'}
          hint={showCost ? 'At moving average cost' : 'Hidden for your role'}
        />
      </SummaryGrid>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0,1fr)', lg: 'minmax(0,1fr) 300px' },
          gap: 3,
          alignItems: 'start',
        }}
      >
        <Card>
          <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
            <Typography variant="h5">Items</Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Item</TableCell>
                  <TableCell align="right">Qty</TableCell>
                  {showCost && (
                    <>
                      <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        Unit cost
                      </TableCell>
                      <TableCell align="right">Cost</TableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {d.lines.map((l) => (
                  <TableRow key={l.id} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {l.description}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {l.sku}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Qty value={l.qty} unit={l.unitSymbol} />
                    </TableCell>
                    {showCost && (
                      <>
                        <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                          <Money value={l.unitCost} />
                        </TableCell>
                        <TableCell align="right">
                          <Money value={l.totalCost} />
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
        <Card sx={{ p: 2.5 }}>
          <Typography variant="overline" color="text.secondary">
            Delivered to
          </Typography>
          <Typography variant="h5" sx={{ mt: 0.5 }}>
            {d.contact.name}
          </Typography>
          {d.contact.phone && <Typography color="text.secondary">{d.contact.phone}</Typography>}
          <Box sx={{ mt: 2 }}>
            <InfoRow label="Posted">{dateTime(d.postedAt, format)}</InfoRow>
          </Box>
          {d.notes && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                Notes
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                {d.notes}
              </Typography>
            </Box>
          )}
        </Card>
      </Box>

      <ReasonDialog
        open={reverseOpen}
        title={`Reverse ${d.number}?`}
        body={
          <>
            The items go back into stock at {d.location.name} at the cost they left with, and the
            cost of goods entry is reversed.
            {d.salesOrderId
              ? ' The order gets the quantity (and its reservation) back.'
              : ' A counter-sale delivery can only be reversed after its invoice is cancelled.'}
          </>
        }
        confirmLabel="Reverse delivery"
        onClose={() => setReverseOpen(false)}
        onConfirm={(reason) => reverse.mutateAsync(reason)}
      />
      <AttachmentsPanel recordType="DELIVERY" recordId={d.id} sx={{ mt: 3 }} />
      <WhatsappHistory documentType="delivery" documentId={d.id} sx={{ mt: 3 }} />
      <EmailHistory documentType="delivery" documentId={d.id} sx={{ mt: 3 }} />
      {element}
    </Box>
  );
}
